import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, m } from 'framer-motion';

import { breakpoints, screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { RichHeading } from '@/components/common/Heading';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Tag } from '@/components/common/Tag';
import { PortableText, transientOptions } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { getBulletPointColour, getTextColour } from '@/lib/colourUtils';
import { useMatchMedia } from '@/lib/useMatchMedia';
import { bodySmall, bodySmallBold } from '@/styles/typography';

import type { TextColourTokenType } from '@/types/colours';
import type { SanityAccordionWithImageType } from '@/types/sanity';

export interface AccordionWithImageProps extends SanityAccordionWithImageType {}

const AccordionWithImage = ({
  tag,
  headingCaption,
  heading,
  backgroundColour,
  primaryImage,
  bulletPoints,
  accordion,
  isCaptionUppercase = true,
}: AccordionWithImageProps) => {
  const [openItems, setOpenItems] = useState([0]);
  const accordionRef = useRef(null);
  const [accordionHeight, setAccordionHeight] = useState(300);
  const isMobile = useMatchMedia(breakpoints.md);
  const isPrimaryImageSet = primaryImage && !!primaryImage.asset?._ref;
  const backgroundColourToken =
    backgroundColour?.token || '--background-primary';
  const bulletPointColourToken = getBulletPointColour(backgroundColourToken);

  const handleToggleItem = (idx) => {
    if (isMobile) {
      if (openItems.includes(idx)) {
        setOpenItems((prev) => prev.filter((item) => item !== idx));
      } else {
        setOpenItems((prev) => [...prev, idx]);
      }
    } else {
      setOpenItems([idx]);
    }
  };
  useEffect(() => {
    if (accordionRef.current) {
      const { height } = accordionRef.current.getBoundingClientRect();
      setAccordionHeight(height);
    }
  }, []);
  const { style } = heading[0];
  let headingSize = null;

  // This was to address introducing heading(includeStyles = true) into accordion schema, to not break typography of already existing blocks
  if (style === 'normal') {
    headingSize = 'headline-sm';
  }

  return (
    <Section
      backgroundColour={backgroundColourToken}
      colour={getTextColour(backgroundColourToken)}
      verticalPadding
    >
      {tag?.tagText && (
        <Tag bgColour={tag?.tagColour} margin="0 0 var(--spacing-xxx-small) 0">
          {tag.tagText}
        </Tag>
      )}
      {headingCaption && (
        <HeadingCaption
          isCaptionUppercase={isCaptionUppercase}
          textColour={getTextColour(backgroundColourToken, {
            action: false,
            isCaption: true,
          })}
          size="caption-lg"
        >
          {headingCaption}
        </HeadingCaption>
      )}
      <HeadingLevelProvider level={2}>
        <RichHeading size={headingSize} heading={heading} />
        <PortableTextContainer>
          <BulletPoints pointColour={bulletPointColourToken}>
            <PortableText value={bulletPoints} />
          </BulletPoints>
        </PortableTextContainer>
        <HeadingLevel>
          <AccordionWrapper hasBulletPoints={!!bulletPoints}>
            <AccordionInner ref={accordionRef}>
              {accordion.map((accordionItem, idx) => (
                <AccordionItem
                  {...accordionItem}
                  idx={idx}
                  key={accordionItem._key}
                  handleToggle={handleToggleItem}
                  openItems={openItems}
                  primaryImageSet={isPrimaryImageSet}
                  textColour={getTextColour(backgroundColourToken)}
                />
              ))}
            </AccordionInner>
            {!isPrimaryImageSet ? (
              <>
                {!isMobile &&
                  openItems.length === 0 &&
                  accordion[0].itemImage?.asset && (
                    <ImageWrapper accordionHeight={accordionHeight}>
                      <StyledImage
                        {...accordion[0].itemImage}
                        objectFit="cover"
                      />
                    </ImageWrapper>
                  )}
                {!isMobile &&
                  openItems.length >= 1 &&
                  accordion[openItems[0]].itemImage?.asset && (
                    <ImageWrapper accordionHeight={accordionHeight}>
                      <StyledImage
                        {...accordion[openItems[0]].itemImage}
                        objectFit="cover"
                        radius="l"
                        sizes="(max-width: 960px) 100vw, 50vw"
                      />
                    </ImageWrapper>
                  )}
              </>
            ) : (
              !isMobile && (
                <ImageWrapper accordionHeight={accordionHeight}>
                  <StyledImage {...primaryImage} objectFit="cover" radius="l" />
                </ImageWrapper>
              )
            )}
          </AccordionWrapper>
        </HeadingLevel>
      </HeadingLevelProvider>
    </Section>
  );
};
const PortableTextContainer = styled.div`
  margin-top: var(--spacing-xx-small);
  margin-bottom: var(--spacing-xx-small);
`;
const HeadingCaption = styled(Paragraph)<{
  textColour: TextColourTokenType;
  isCaptionUppercase: boolean;
}>`
  margin-bottom: var(--spacing-xxx-small);
  color: ${({ textColour }) => `var(${textColour})`};
  text-transform: ${({ isCaptionUppercase }) =>
    isCaptionUppercase ? 'uppercase' : 'none'};
`;

const AccordionWrapper = styled.div<{
  hasBulletPoints: boolean;
}>`
  padding-top: ${(props) =>
    props.hasBulletPoints ? 'var(--spacing-small)' : 'var(--spacing-xx-small)'};

  position: relative;

  ${screen.md} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-400);
  }

  ${screen.lg} {
    gap: var(--spacing-500);
  }
`;

const AccordionInner = styled.div``;

const ImageWrapper = styled.div<{ accordionHeight: number }>`
  display: none;
  & > img {
    height: ${(props) => `${props.accordionHeight}px`};
  }
  ${screen.md} {
    display: block;
  }

  ${screen.lg} {
    & > img {
      max-height: 525px;
    }
  }
`;

interface AccordionItemProps {
  _key: string;
  itemHeading: string;
  itemBody: string;
  itemImage?: any;
  trackingTag?: { blockName: string; blockAction: string };
  idx: number;
  openItems: number[];
  primaryImageSet: boolean;
  textColour: TextColourTokenType;
  handleToggle: (idx) => void;
}

const AccordionItem = ({
  _key,
  itemHeading,
  itemBody,
  itemImage,
  trackingTag,
  idx,
  openItems,
  primaryImageSet,
  textColour,
  handleToggle,
}: AccordionItemProps) => {
  const isOpen = useMemo(() => openItems.includes(idx), [idx, openItems]);

  return (
    <AccordionItemWrapper borderColour={textColour}>
      <Heading size="body-md-semi-bold" fontWeight="semi-bold">
        <AccordionItemButton
          type="button"
          aria-label={itemHeading}
          aria-expanded={openItems.includes(idx)}
          aria-controls={`item${_key}`}
          id={`accordion${_key}id`}
          onClick={() => handleToggle(idx)}
          textColour={textColour}
          className={
            trackingTag?.blockName &&
            `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
          }
        >
          <ItemHeading>{itemHeading}</ItemHeading>
          <StyledIcon icon="ChevronRight" $isOpen={isOpen} fill={textColour} />
        </AccordionItemButton>
      </Heading>
      <AnimatePresence initial={false}>
        {isOpen && (
          <m.div
            id={`item${_key}`}
            aria-labelledby={`accordion${_key}id`}
            key="content"
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              closed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <ItemParagraph textColour={textColour}>{itemBody}</ItemParagraph>
            {!primaryImageSet && itemImage?.asset && (
              <ItemImageWrapper
                variants={{
                  closed: { opacity: 0.6, scale: 0.9 },
                  open: { opacity: 1, scale: 1 },
                }}
                transition={{ duration: 0.5 }}
              >
                <StyledImage
                  {...itemImage}
                  sizes="(max-width: 960px) 100vw, 300px"
                  objectFit="cover"
                  radius="l"
                />
              </ItemImageWrapper>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </AccordionItemWrapper>
  );
};

const AccordionItemWrapper = styled.div<{ borderColour: TextColourTokenType }>`
  padding: var(--spacing-small) 0;
  min-height: var(--spacing-800);
  &:not(:last-of-type) {
    border-bottom: 1px solid
      ${(props) =>
        props.borderColour === '--text-inverse-strong'
          ? 'var(--border-inverse-strong)'
          : 'var(--border-warm-subtle)'};
  }
`;

const StyledImage = styled(Image)``;

const ItemImageWrapper = styled(m.div)`
  margin-top: var(--spacing-small);
  & > ${StyledImage} {
    max-height: 200px;
  }
  ${screen.sm} {
    & > ${StyledImage} {
      max-height: 300px;
    }
  }

  ${screen.md} {
    display: none;
  }
`;

const AccordionItemButton = styled.button<{ textColour: TextColourTokenType }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: var(--transparent);
  border: none;
  text-align: left;
  color: ${(p) => `var(${p.textColour})`};
  cursor: pointer;
`;

const ItemHeading = styled.span`
  max-width: 1055px;
  ${bodySmallBold}
  padding-right: var(--spacing-50);
`;

interface StyledChevronProps {
  $isOpen: boolean;
  fill: TextColourTokenType;
}

const StyledIcon = styled(Icon, transientOptions)<StyledChevronProps>`
  flex-shrink: 0;
  transform: rotate(
    ${(chevronprops) => (chevronprops.$isOpen ? '270deg' : '90deg')}
  );
  transition: transform 0.3s
    ${(chevronprops) => (chevronprops.$isOpen ? 'ease-in' : 'ease-out')};
  fill: ${({ fill }) => `var(${fill})`};
`;

const ItemParagraph = styled(Paragraph)<{ textColour: TextColourTokenType }>`
  color: ${(p) => `var(${p.textColour})`};
  ${bodySmall}
  padding-right: var(--spacing-350);
  padding-top: var(--spacing-xxx-small);
`;

export { AccordionWithImage };
